import React, { useCallback, useMemo, useState } from 'react'
import { IconClose, IconSelectListReadonly } from '../icon/icon'

const useCallbackDefault = (fn, defaultValue) => {
  return useCallback(
    item => {
      if (fn) {
        return fn(item)
      } else {
        return defaultValue
      }
    },
    [fn, defaultValue]
  )
}

// fnHint (optional): a short note on a read-only tag, e.g. "Always included":
// its tooltip and what a screen reader hears. A read-only tag shows a lock,
// not the sentence, so the row stays compact (owner feedback 2026-10-06). An active, editable tag can always be removed while the
// control is enabled, even when nothing is left to add.
export const SelectTag = ({ selectText, data, enabled, fnLabel, fnActive, fnReadOnly, fnHint, onAdd, onRemove }) => {
  const [search, updateSearch] = useState('')

  const label = useCallbackDefault(fnLabel, '')
  const active = useCallbackDefault(fnActive, false)
  const readOnly = useCallbackDefault(fnReadOnly, false)
  const hint = useCallbackDefault(fnHint, '')

  const items = useMemo(() => data || [], [data])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeList = useMemo(() => items.filter(item => active(item)), [items])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeListReadOnly = useMemo(() => activeList.filter(item => readOnly(item)), [activeList])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeListEditable = useMemo(() => activeList.filter(item => !readOnly(item)), [activeList])

  const dropdownList = useMemo(
    () => {
      if (!items) {
        return []
      }

      return items.filter(item => !active(item) && !readOnly(item))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, search]
  )

  const handleAdd = item => {
    onAdd && onAdd(item)
  }

  const handleRemove = item => {
    onRemove && onRemove(item)
    updateSearch('')
  }

  const disabled = useMemo(
    () => {
      if (!enabled) {
        return true
      }

      if (dropdownList.length === 0) {
        return true
      }

      return false
    },
    [enabled, dropdownList.length]
  )

  return (
    <div className="input-group">
      <button className="btn btn-outline-secondary dropdown-toggle text-capitalize" disabled={disabled} type="button" data-bs-toggle="dropdown">
        {selectText || 'select'}
      </button>

      <div className="form-control d-flex flex-wrap p-0">
        {activeListReadOnly.map((item, i) => <Item key={i} item={item} label={label} hint={hint(item)} disabled />)}
        {activeListEditable.map((item, i) => <Item key={i} item={item} label={label} disabled={!enabled} onRemove={handleRemove} />)}
      </div>

      <ul className="dropdown-menu">
        {dropdownList.map((item, i) =>
          <li key={i} style={{ cursor: 'pointer' }} onClick={() => handleAdd(item)}>
            <span className="dropdown-item">
              {item.roleName}
            </span>
          </li>
        )}
      </ul>
    </div>
  )
}

const Item = ({ item, label, hint, disabled, onRemove }) => {
  const handleRemove = e => {
    e.stopPropagation()
    onRemove && onRemove(item)
  }

  return (
    <div
      className={`rounded ${disabled ? 'bg-secondary' : 'bg-primary'} text-light text-nowrap p-1 m-1`}
      title={hint || undefined}
      data-testid="select-tag-item"
      data-readonly={disabled ? 'true' : 'false'}>
      <span className="mx-1">
        {label(item)}
      </span>
      {disabled && <IconSelectListReadonly className="me-1 opacity-75" />}
      {hint && <span className="visually-hidden">{hint}</span>}
      {!disabled &&
        <span style={{ cursor: 'pointer' }} className="" role="button" aria-label={`remove ${label(item)}`} onClick={handleRemove}>
          <IconClose />
        </span>}
    </div>
  )
}
